import { Transformer } from 'markmap-lib';
import { Markmap } from 'markmap-view';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { cx } from '../../cx';
import { Icon } from '../../icon-svg';
import { useOpenModal } from '../../next-modal';
import { useIsDarkMode } from '../../utils';
import { useChatTranslation } from '../hooks/use-i18n';

const transformer = new Transformer();

const processAiContent = (content: string) => {
  return content.replace(/\{\{ref:(\d+)\}\}/g, '[$1]');
};

interface MindMapProps {
  ai: string;
  className?: string;
  focusStart?: boolean;
}
export const MindMap: FC<MindMapProps> = memo(({ ai, className, focusStart = true }) => {
  const isDark = useIsDarkMode();
  const svgRef = useRef<SVGSVGElement>(null);
  const markmapRef = useRef<Markmap | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!svgRef.current || !ai) return;

    const processedAi = processAiContent(ai);
    const { root } = transformer.transform(processedAi);

    if (!markmapRef.current) {
      markmapRef.current = Markmap.create(svgRef.current, {
        autoFit: false,
      });
    }

    const mm = markmapRef.current;
    void mm.setData(root).then(() => {
      if (!focusStart) {
        void mm.fit().then(() => {
          mm.zoom.translateBy(mm.svg as any, 0, -180);
          setIsLoading(false);
        });

        return;
      }
      void mm.rescale(0.7).then(() => {
        const { y1, y2 } = mm.state.rect;
        const centerY = (y1 + y2) / 2;
        mm.zoom.translateTo(mm.svg as any, 200, centerY);
        setIsLoading(false);
      });
    });
  }, [ai, focusStart]);

  return (
    <div
      className={cx(
        `w-full overflow-hidden`,
        className,
        isLoading && 'opacity-0',
        isDark && 'markmap-dark'
      )}
    >
      <svg ref={svgRef} className="w-full h-full" />
    </div>
  );
});

export const useOpenMindMap = () => {
  const openModal = useOpenModal();
  const { t } = useChatTranslation();

  return useCallback(
    ({ ai, focusStart = false }: { ai: string; focusStart?: boolean }) => {
      openModal.modal({
        content: (_) => (
          <>
            <div className="flex justify-between p-4">
              <h3 className="text-t1-medium">{t('chatSidebarMindMap')}</h3>
              <Icon name="IcClose" size="small" onClick={() => _.onCloseModal()} />
            </div>
            <MindMap className="h-screen w-[80vw] bg-white1" ai={ai} focusStart={focusStart} />
          </>
        ),
      });
    },
    [openModal, t]
  );
};
